import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Tooltip from 'apollo-react/components/Tooltip';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import React, { useContext } from 'react';

import SSHyperLink from './ssHyperLink/SSHyperLinkControl';
import SSScormFileReadOnly from './ssScormFileReadOnly';
import LazyText from '../../components/LazyText';
import RuntimeTranslation from '../../components/RuntimeTranslation';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import { PanelContext } from '../../utils/PanelConfig';
import { isEmpty } from '../../utils/ramdaUtils';
import ControlChangesIcon from '../components/ControlChangesIcon';
import {
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
  ZOOM_TYPES,
} from '../constants/form-manager.constants';
import { FCM_BORDER_STYLES, FCM_HIDDEN_CONTROL_STYLES } from '../constants/runtime.constants';
import {
  getReadOnlyControlContent,
  getTextViewValue,
  isDataTypeDate,
} from '../selectors/form-manager.selectors';

const useStyles = makeStyles({
  styledLabel: {
    height: 21,
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontWeight: 400,
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: '#333',
    wordBreak: 'break-word',
  },
  styledLabelEditor: {
    marginRight: 10,
  },
  styledLabelDetail: {
    flex: 1,
    maxWidth: '33%',
    flexBasis: '33%',
    alignSelf: 'flex-start',
  },
  styledValue: {
    alignSelf: 'flex-start',
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontStretch: 'normal',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: '#333',
    '[class*=SSTable-] &': {
      display: 'inline-block',
    },
    '& + &': {
      paddingLeft: 5,
    },
    '& [class=public-DraftEditor-content]': {
      minHeight: 'unset',
      padding: 0,
    },
  },
  styledValueEditor: {
    height: 'unset',
    whiteSpace: 'nowrap',
  },
  styledValueDetail: {
    flex: 1,
    maxWidth: '66%',
    flexBasis: '66%',
  },
  headerEntry: {
    display: 'inline-flex',
  },
  headerLabel: {
    padding: '3px 3px 0 0',
    border: 0,
    textAlign: 'left',
    display: 'inline-block',
    whiteSpace: 'normal',
    boxSizing: 'border-box',
    wordWrap: 'break-word',
    lineHeight: '1.5em',
    fontSize: 14,
    marginRight: 5,
  },
  changesModeWrapper: {
    marginRight: 8,
    padding: '0 8px',
    border: FCM_BORDER_STYLES.CONDITIONAL,
  },
  hiddenControl: FCM_HIDDEN_CONTROL_STYLES,
  headerValue: {
    padding: '3px 32px 0 0',
    fontStyle: 'normal',
    fontSize: 14,
  },
  fixedLabelContainer: { minWidth: 160, marginRight: 10 },
  valueContainer: {
    display: 'flex',
  },
  ssRadioButtonList: {
    maxWidth: 576,
  },
  mainContainerEditor: {
    flexWrap: 'nowrap',
  },
});

const rendererContent = (renderer, content) =>
  _.isFunction(renderer) ? renderer(content) : content;

const renderValue = ({ value, tooltip }) => {
  return tooltip ? (
    <Tooltip variant="light" title={tooltip} placement="bottom">
      <span>{value}</span>
    </Tooltip>
  ) : (
    value
  );
};

const ValueToRender = ({
  contentToDisplay,
  showTranslation,
  textToRender,
  valueProps,
  isDetail,
  isEditor,
  inTable,
  classes,
  value,
  tooltip,
  uita,
}) => {
  if (isDetail && inTable && textToRender === '---') {
    return null;
  }

  return (
    <Grid item {...valueProps} className={classes.valueContainer}>
      {renderValue({
        value: (
          <LazyText text={contentToDisplay}>
            <span
              className={classNames(
                classes.styledValue,
                isEditor && textToRender === '---' ? classes.styledValueEditor : ''
              )}
              ui-ta={uita || 'value'}
              title={tooltip}
              data-is-read-only={true}
            >
              {contentToDisplay}
            </span>
          </LazyText>
        ),
        tooltip,
      })}
      {showTranslation && <RuntimeTranslation value={value} isReadOnly />}
    </Grid>
  );
};

const ReadOnlyInputControl = (props) => {
  const defaultLabel = '';
  const defaultValue = '---';
  const {
    label = defaultLabel,
    value: untranslatedValue = defaultValue,
    uita = '',
    renderText,
    tooltip = null,
    isInHeader,
    isDetail,
    inTable,
    entityName,
    fieldName,
    controlType,
    isEditor,
    inFieldGroup,
    isHiddenLabel,
    id,
    index,
    parentType,
    formatType,
    url,
    containerStyles,
    isOnline,
    isControlHiddenFCM,
    isControlAddedFCM,
    isControlChangedFCM,
  } = props;
  const hiddenLabel = inTable || (inFieldGroup && isHiddenLabel);

  const classes = useStyles({ isControlAddedFCM });
  const translateString = useTranslation();
  const layoutContext = useContext(PanelContext);
  const isDate = useFormSelector(isDataTypeDate, { dataMember: entityName, dataField: fieldName });
  const textViewValue = useFormSelector(getTextViewValue, {
    dataMember: entityName,
    dataField: fieldName,
    value: untranslatedValue,
    id,
    index,
    formatType,
    prop: FORM_MODEL_ATTRIBUTES.TRANSLATE_DATA,
    controlType,
    translateString,
  });

  const isTextView = controlType === SS_CONTROL_TYPE.TEXT_VIEW;
  const value = isTextView ? textViewValue : untranslatedValue;
  const showTranslation =
    controlType === SS_CONTROL_TYPE.TEXT_AREA && value && value !== defaultValue;

  const content = useFormSelector(getReadOnlyControlContent, { value, defaultValue, controlType });

  if (isTextView && parentType === SS_CONTROL_TYPE.GRID_COLUMN && value === '---') {
    return renderValue({ value: '', tooltip });
  }

  if (ZOOM_TYPES.includes(controlType)) {
    return null;
  }

  if (
    hiddenLabel &&
    controlType !== SS_CONTROL_TYPE.CHECK_BOX &&
    (_.isNil(value) || isEmpty(value))
  ) {
    return renderValue({ value: '', tooltip });
  }

  if (controlType === SS_CONTROL_TYPE.SCORM_FILE_INPUT) {
    return <SSScormFileReadOnly {...props} />;
  }

  const isValidDate = isDate && moment(content).isValid() && !isTextView;
  const contentToRender = isValidDate ? moment(content).format('DD-MMM-YYYY') : content;
  const textToRender = rendererContent(renderText, contentToRender);
  const isTextViewHyperLink =
    controlType === SS_CONTROL_TYPE.TEXT_VIEW && url && textToRender !== '---';

  const contentToDisplay = isTextViewHyperLink ? (
    <SSHyperLink textToRender={textToRender} isTextViewHyperLink {...props} isOnline={isOnline} />
  ) : (
    textToRender
  );

  if (hiddenLabel) {
    // show value only
    return renderValue({
      value: (
        <span className={classes.styledValue} ui-ta={uita} data-is-read-only={true}>
          {contentToDisplay}
          {showTranslation && <RuntimeTranslation value={value} isReadOnly />}
        </span>
      ),
      tooltip,
    });
  }

  if (isInHeader) {
    let dateValue;
    if (isDate && moment(value).isValid() && !isTextView) {
      dateValue = moment(value).format('DD-MMM-YYYY');
    }

    const valueToRender = dateValue ?? content ?? value ?? defaultValue;

    return (
      <span
        className={classNames(
          classes.headerEntry,
          isControlAddedFCM || isControlChangedFCM ? classes.changesModeWrapper : ''
        )}
        ui-ta={getUITA('EntityDetailHeaderEntry')}
        data-ss-id={id}
      >
        <span
          className={classNames(
            classes.headerLabel,
            isControlHiddenFCM ? classes.hiddenControl : ''
          )}
          data-hidden-fcm={isControlHiddenFCM}
        >{`${label.trim()}:`}</span>
        {renderValue({
          value: (
            <LazyText text={valueToRender}>
              <span
                className={classNames(
                  classes.headerValue,
                  isEditor && valueToRender === '---' ? classes.styledValueEditor : '',
                  isControlHiddenFCM ? classes.hiddenControl : ''
                )}
                data-hidden-fcm={isControlHiddenFCM}
              >
                {valueToRender}
              </span>
            </LazyText>
          ),
          tooltip,
        })}
        {isControlChangedFCM && <ControlChangesIcon id={id} />}
      </span>
    );
  }

  if (((isDetail || inTable) && label?.trim() === '') || (inTable && value === '---')) {
    return null;
  }

  // show label and value
  const labelProps = isEditor
    ? {}
    : layoutContext.layout === 'md'
    ? {
        className: (classes.fixedLabelContainer, !isInHeader ? classes.styledLabelDetail : ''),
      }
    : { xs: 4 };

  const valueProps = isEditor
    ? {}
    : layoutContext.layout === 'md'
    ? {
        className: (classes.valueContainer, !isInHeader ? classes.styledValueDetail : ''),
      }
    : { xs: 8 };

  const renderValueProps = {
    contentToDisplay,
    showTranslation,
    textToRender,
    valueProps,
    isDetail,
    isEditor,
    inTable,
    classes,
    value,
    tooltip,
    uita,
  };

  return (
    <Grid
      container
      spacing={1}
      ui-ta={uita}
      data-is-read-only={true}
      className={
        containerStyles ??
        classNames(
          {
            [classes.ssRadioButtonList]:
              controlType === SS_CONTROL_TYPE.RADIO_BUTTON_LIST && !isDetail,
          },
          isEditor ? classes.mainContainerEditor : ''
        )
      }
    >
      {!inTable && (
        <Grid item {...labelProps}>
          <span
            className={classNames(classes.styledLabel, isEditor ? classes.styledLabelEditor : '')}
          >
            {label.trim()}
          </span>
        </Grid>
      )}
      <ValueToRender {...renderValueProps} />
    </Grid>
  );
};

export default ReadOnlyInputControl;
